import { useCallback, useEffect, useState } from 'react';

import { LobbyScreen } from './screens/LobbyScreen';
import { LoginScreen } from './screens/LoginScreen';
import { PrejoinScreen } from './screens/PrejoinScreen';
import { RoomScreen } from './screens/RoomScreen';
import { LeftScreen } from './screens/LeftScreen';
import { BuildTag } from './components/BuildTag';
import { useSession } from './lib/useSession';
import type { JoinCredentials } from './lib/api';
import type { CodecPreference } from './lib/codec';
import type { AudioMode } from './lib/audio';
import type { VideoMode } from './lib/video';

export interface JoinIntent {
  credentials: JoinCredentials;
  displayName: string;
  cameraEnabled: boolean;
  micEnabled: boolean;
  cameraId: string;
  micId: string;
  speakerId: string;
  codec: CodecPreference;
  videoMode: VideoMode;
  audioMode: AudioMode;
  noiseSuppression: boolean;
}

type Stage =
  | { name: 'lobby' }
  | { name: 'login' }
  | { name: 'prejoin'; code: string }
  | { name: 'room'; intent: JoinIntent }
  | { name: 'left'; code: string; reason: 'user' | 'error'; message?: string };

/** Код встречи из адреса вида `/j/khtm-p8rq-x4wd`. */
function codeFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/j\/([a-z0-9-]+)\/?$/i);
  return match ? match[1].toLowerCase() : null;
}

function stageFromLocation(): Stage {
  const code = codeFromPath(window.location.pathname);
  if (code) return { name: 'prejoin', code };
  if (window.location.pathname === '/login') return { name: 'login' };
  return { name: 'lobby' };
}

export function App() {
  const session = useSession();
  const [stage, setStage] = useState<Stage>(stageFromLocation);

  const navigate = useCallback((path: string, next: Stage) => {
    if (window.location.pathname !== path) window.history.pushState({}, '', path);
    setStage(next);
  }, []);

  // Кнопки «назад/вперёд» в браузере не должны ломать состояние.
  useEffect(() => {
    const onPop = () => setStage(stageFromLocation());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Предупреждение при закрытии вкладки во время встречи.
  useEffect(() => {
    if (stage.name !== 'room') return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [stage.name]);

  const goHome = useCallback(() => navigate('/', { name: 'lobby' }), [navigate]);

  return (
    <>
      {renderStage()}
      <BuildTag />
    </>
  );

  function renderStage() {
  switch (stage.name) {
    case 'lobby':
      return (
        <LobbyScreen
          session={session}
          onOpenRoom={(code) => navigate(`/j/${code}`, { name: 'prejoin', code })}
          onSignIn={() => navigate('/login', { name: 'login' })}
        />
      );

    case 'login':
      return (
        <LoginScreen
          domain={session.domain}
          onSignedIn={async () => {
            await session.refresh();
            goHome();
          }}
          onBack={goHome}
        />
      );

    case 'prejoin':
      return (
        <PrejoinScreen
          code={stage.code}
          onJoin={(intent) => setStage({ name: 'room', intent })}
          onBack={goHome}
        />
      );

    case 'room':
      return (
        <RoomScreen
          intent={stage.intent}
          onLeave={(reason, message) =>
            setStage({ name: 'left', code: stage.intent.credentials.room, reason, message })
          }
        />
      );

    case 'left':
      return (
        <LeftScreen
          code={stage.code}
          reason={stage.reason}
          message={stage.message}
          onRejoin={() => navigate(`/j/${stage.code}`, { name: 'prejoin', code: stage.code })}
          onHome={goHome}
        />
      );
  }
}
}
